import { useMutation, useQuery } from "@tanstack/react-query";
import {
  deleteExpense,
  getExpenseDescriptions,
  getExpenses,
  getExpenseSuppliers,
  getExpenseType,
  getExpenseUnits,
  updateExpense,
} from "../api/expense";
import { AxiosRequestConfig } from "axios";
import { PaginatedApiResponse, PaginationInfo } from "@/types/api";
import { Expense } from "@/types/expense";
import { EditExpenseFormValues } from "../components/ExpenseEditModal";
import { ApiResponse } from "@/types/api";
import { ExpenseType } from "@/types/expenseType";
import { ExpenseDescription } from "@/types/expenseDescription";
import { ExpenseUnit } from "@/types/expenseUnit";
import { Supplier } from "@/types/supplier";

interface UseGetExpensesProps {
  config: AxiosRequestConfig;
  enabled?: boolean;
}

interface UseExpenseMutationOptions<T> {
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
}

export const useGetExpenses = (config?: AxiosRequestConfig) => {
  const params = config?.params;

  return useQuery<
    PaginatedApiResponse<Expense[]>,
    Error,
    { data: Expense[]; pagination: PaginationInfo }
  >({
    queryKey: [
      "expenses",
      params?.date_between,
      params?.sort,
      params?.filters,
      params?.limit,
      params?.page,
      params?.offset,
    ],
    queryFn: () => getExpenses(config),
    select: (response) => ({
      data: response.data,
      pagination: response.pagination,
    }),
  });
};

export const useGetExpensesOptions = (config?: AxiosRequestConfig) => {
  const params = config?.params;

  return useQuery<ApiResponse<Expense[]>, Error, Expense[]>({
    queryKey: ["expensesOptions", params?.date_between, params?.limit],
    queryFn: () => getExpenses(config),
    select: (response) => response.data,
  });
};

export const useUpdateExpense = (
  options?: UseExpenseMutationOptions<Expense>
) => {
  return useMutation<
    Expense,
    Error,
    { id: string; payload: EditExpenseFormValues }
  >({
    mutationFn: ({ id, payload }) => updateExpense(id, payload),
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useDeleteExpense = (
  options?: UseExpenseMutationOptions<Expense>
) => {
  return useMutation<Expense, Error, string>({
    mutationFn: (id) => deleteExpense(id),
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useGetExpenseType = ({ config, enabled }: UseGetExpensesProps) => {
  return useQuery<ApiResponse<ExpenseType>, Error, ExpenseType>({
    queryKey: ["expense-type", config.params],
    queryFn: () => getExpenseType(config.params.id),
    select: (response) => response.data,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  });
};

export const useGetExpenseDescriptions = ({
  config,
  enabled,
}: UseGetExpensesProps) => {
  return useQuery<
    ApiResponse<ExpenseDescription[]>,
    Error,
    ExpenseDescription[]
  >({
    queryKey: ["expense-descriptions", config.params],
    queryFn: () => getExpenseDescriptions(config),
    select: (response) => response.data,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  });
};

export const useGetExpenseUnits = ({
  config,
  enabled,
}: UseGetExpensesProps) => {
  return useQuery<ApiResponse<ExpenseUnit[]>, Error, ExpenseUnit[]>({
    queryKey: ["expense-units", config.params],
    queryFn: () => getExpenseUnits(config),
    select: (response) => response.data,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  });
};

export const useGetExpenseSuppliers = ({
  config,
  enabled,
}: UseGetExpensesProps) => {
  return useQuery<ApiResponse<Supplier[]>, Error, Supplier[]>({
    queryKey: ["expense-suppliers", config.params],
    queryFn: () => getExpenseSuppliers(config),
    select: (response) => response.data,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  });
};
